import { FunctionComponent, useEffect, useState } from 'react';
import { Skeleton } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import { AccountType, UserTradingSystemCredentials } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { TradersLoginProps } from './traders-login';
import { TradersLoginCard } from './traders-login.card';

export const TraderLoginContent: FunctionComponent<TradersLoginProps> = ({
  userId,
  tradingSystem,
  accountId,
  contest,
}) => {
  const {
    dataStore: { userStore, contestAccountStore, accountStore },
  } = useStore();
  const [tsUser, setTsUser] = useState<UserTradingSystemCredentials | null>(
    null,
  );

  const { isLoading, updateLoadingState } = useLoading();

  const tsUsers = userStore.currentUser.tsUsers || [];

  const selectTsUser = async (logins: UserTradingSystemCredentials[]) => {
    try {
      updateLoadingState(LoadingState.Loading);

      const currentUser = logins.find((user) => {
        return (
          user.tradingSystem === tradingSystem &&
          user.externalId.toString() === userId.toString()
        );
      });

      if (currentUser) {
        const decryptedPassword = await accountStore.decryptPassword(
          currentUser.login,
          currentUser.tradingSystem,
        );

        currentUser.password = decryptedPassword || '';

        setTsUser(currentUser);

        return true;
      }

      return false;
    } catch (e) {
      console.error('Failed to get trading system user', e);
      return false;
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const handleContest = async () => {
    try {
      const { login, password } = await contestAccountStore.getCredentials(
        accountId as string,
      );

      setTsUser({
        login,
        password,
        externalId: accountId as string,
        accountType: AccountType.Competition,
        tradingSystem,
      });
    } catch (e) {
      console.error('Failed to get trading system user', e);
      return false;
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (contest) {
        await handleContest();
        return;
      }
      const userFound = await selectTsUser(tsUsers);

      if (!userFound) {
        userStore.updateLogins().then((logins) => {
          selectTsUser(logins || []);
        });
      }
    };
    init();
  }, []);

  if (isLoading) {
    return <Skeleton active={true} />;
  }

  if (!tsUser) {
    return null;
  }

  return (
    <TradersLoginCard
      login={tsUser.login}
      password={tsUser.password}
      tradingSystem={tsUser.tradingSystem}
    />
  );
};
